import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import LanguageSwitcher from './LanguageSwitcher'
import logoRot from '../assets/logo-rot.webp'

export default function Navbar() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const prefix = pathname === '/' ? '' : '/'
  const navLinks = [
    { label: t('nav.services'), href: `${prefix}#services` },
    { label: t('nav.about'), href: `${prefix}#team` },
    { label: t('nav.references'), href: `${prefix}#references` },
    { label: t('nav.contact'), href: `${prefix}#contact` },
  ]

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-sand/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a
            href="/"
            className="block"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img src={logoRot} alt="buenas studio" className="h-14" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`font-body text-base transition-colors ${scrolled ? 'text-espresso hover:text-rot' : 'text-sand hover:text-rot'}`}
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-2">
              <a
                href="https://www.instagram.com/buenas.studio"
                target="_blank"
                rel="noopener noreferrer"
                className={`transition-colors ${scrolled ? 'text-espresso hover:text-rot' : 'text-sand hover:text-rot'}`}
                aria-label="Instagram @buenas.studio"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="5" />
                  <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/buenas-studio/"
                target="_blank"
                rel="noopener noreferrer"
                className={`transition-colors ${scrolled ? 'text-espresso hover:text-rot' : 'text-sand hover:text-rot'}`}
                aria-label="LinkedIn buenas studio"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                >
                  <rect x="2" y="2" width="20" height="20" rx="4" ry="4" />
                  <line x1="8" y1="11" x2="8" y2="16" />
                  <line x1="8" y1="8" x2="8" y2="8.01" />
                  <path d="M12 16v-5M16 16v-3a2 2 0 0 0-4 0" />
                </svg>
              </a>
            </div>
            <LanguageSwitcher scrolled={scrolled} />
            <a
              href="/#contact"
              className="bg-rot text-sand rounded-full px-5 py-2 text-base font-body hover:bg-lachs transition-colors"
            >
              {t('nav.cta')}
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 z-60 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menü"
          >
            <span
              className={`block w-6 h-0.5 transition-all duration-300 ${
                mobileOpen
                  ? 'rotate-45 translate-y-2 bg-sand'
                  : scrolled
                    ? 'bg-espresso'
                    : 'bg-sand'
              }`}
            />
            <span
              className={`block w-6 h-0.5 transition-all duration-300 ${
                mobileOpen ? 'opacity-0' : scrolled ? 'bg-espresso' : 'bg-sand'
              }`}
            />
            <span
              className={`block w-6 h-0.5 transition-all duration-300 ${
                mobileOpen
                  ? '-rotate-45 -translate-y-2 bg-sand'
                  : scrolled
                    ? 'bg-espresso'
                    : 'bg-sand'
              }`}
            />
          </button>
        </div>
      </nav>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-espresso flex flex-col items-center justify-center gap-8"
          >
            <button
              className="absolute top-6 right-6 cursor-pointer"
              onClick={() => setMobileOpen(false)}
              aria-label="Menü schließen"
            >
              <span className="block w-6 h-0.5 bg-sand rotate-45 translate-y-0.5" />
              <span className="block w-6 h-0.5 bg-sand -rotate-45 -translate-y-0" />
            </button>
            {navLinks.map((link, i) => (
              <motion.a
                key={link.href}
                href={link.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + i * 0.07 }}
                className="font-headline italic text-sand text-3xl hover:text-rot transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </motion.a>
            ))}
            <div className="flex items-center gap-4">
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + navLinks.length * 0.07 }}
                href="https://www.instagram.com/buenas.studio"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sand hover:text-rot transition-colors"
                aria-label="Instagram @buenas.studio"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="5" />
                  <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
                </svg>
              </motion.a>
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + navLinks.length * 0.07 + 0.07 }}
                href="https://www.linkedin.com/company/buenas-studio/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sand hover:text-rot transition-colors"
                aria-label="LinkedIn buenas studio"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6"
                >
                  <rect x="2" y="2" width="20" height="20" rx="4" ry="4" />
                  <line x1="8" y1="11" x2="8" y2="16" />
                  <line x1="8" y1="8" x2="8" y2="8.01" />
                  <path d="M12 16v-5M16 16v-3a2 2 0 0 0-4 0" />
                </svg>
              </motion.a>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + (navLinks.length + 1) * 0.07 }}
              className="mt-2"
            >
              <LanguageSwitcher />
            </motion.div>
            <motion.a
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + (navLinks.length + 2) * 0.07 }}
              href="/#contact"
              className="bg-rot text-sand rounded-full px-8 py-3 text-lg font-body mt-4 hover:bg-lachs transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              {t('nav.cta')}
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
